import React from 'react';
import {
    Text,
    Button,
    VStack,
    Grid,
    Popover,
    PopoverArrow,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
} from '@chakra-ui/react';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { ThemeTokens, FaIcon } from '@acp/core-ui';
import { ReportedFindingDataMachine } from '@acp/shared';
import { ReportedFindingsMachineContext } from '../../machine/context';
import { ReportedFindingsWizardRow } from './reported-findings-wizard-row';

function GridWrapper({ children }: { children: React.ReactNode }) {
    return (
        <Grid
            gridTemplateRows="auto"
            gridTemplateAreas={[
                `
                  "service"
                  "reported-findings"
                  "status"
                  "actions"
                `,
                null,
                null,
                null,
                '"service reported-findings status actions"', // xl
            ]}
            gridTemplateColumns={[
                '1fr', // base
                'min-content 1fr', // small
                null,
                '1fr 1fr 1fr', // large
                '10% 40% 25% 20%', // xl (4-column custom sizes)
            ]}
            gap={3}
            alignItems={{ base: 'center', xl: 'start' }}
            justifyItems="start"
            h="auto"
        >
            {children}
        </Grid>
    );
}

function ToggleTooltipFallback({
    title,
    children,
}: {
    title: string;
    children?: React.ReactNode;
}) {
    return (
        <Popover isLazy trigger="hover" placement="auto">
            <PopoverTrigger>
                <Text color="muted.300" fontWeight="medium" fontSize="sm">
                    {title}
                    <FaIcon icon={faCircleInfo} ml={1} />
                </Text>
            </PopoverTrigger>
            <PopoverContent>
                <PopoverArrow />
                <PopoverBody>
                    <VStack alignItems="flex-start" gap={0}>
                        {children}
                    </VStack>
                </PopoverBody>
            </PopoverContent>
        </Popover>
    );
}

function ToggleActionButton({
    onToggle,
    toggled = false,
    requested = false,
    isToggleable = true,
}: {
    onToggle: () => void;
    toggled?: boolean;
    requested?: boolean;
    isToggleable?: boolean;
}) {
    if (requested) {
        return (
            <Text color="muted.300" fontWeight="medium" fontSize="sm">
                Quote requested
            </Text>
        );
    }

    if (!isToggleable) {
        return (
            <ToggleTooltipFallback title="Not able to be quoted for">
                <Text fontSize="small">
                    Detector Inspector is not able to provide a quote for this reported finding.
                </Text>
            </ToggleTooltipFallback>
        );
    }

    if (toggled) {
        return (
            <Button
                onClick={onToggle}
                borderRightWidth={1}
                colorScheme="secondary"
                variant="solid"
                size="sm"
            >
                Added to quote
            </Button>
        );
    }

    return (
        <Button
            onClick={onToggle}
            colorScheme="secondary"
            background="white"
            borderRightWidth={1}
            borderRightColor="secondary.900"
            borderColor={ThemeTokens.Defaults.borderColor}
            color="muted.700"
            bgColor="white"
            variant="outline"
            size="sm"
        >
            Add to quote
        </Button>
    );
}

function RequestQuoteWizardRow({
    reportedFinding,
    gridWrapper = GridWrapper,
}: {
    reportedFinding: ReportedFindingDataMachine;
    gridWrapper?: React.ComponentType<{ children: React.ReactNode }>;
}) {
    const machine = ReportedFindingsMachineContext.useActorRef();
    const selectedRequestQuoteItems = ReportedFindingsMachineContext.useSelector(
        (state) => state.context.selectedItems
    );
    const { problemRegisterId, canRequestQuote, quoteRequested } = reportedFinding.problemRegister;
    const isQuoted = selectedRequestQuoteItems.includes(problemRegisterId);
    const handleToggle = (reportedFindingId: number) => {
        machine.send({
            type: 'TOGGLE_REPORTED_FINDING',
            reportedFindingId,
        });
    };

    return (
        <ReportedFindingsWizardRow
            reportedFinding={reportedFinding}
            gridWrapper={gridWrapper}
        >
            <ToggleActionButton
                onToggle={() => handleToggle(problemRegisterId)}
                toggled={isQuoted}
                isToggleable={canRequestQuote}
                requested={quoteRequested}
            />
        </ReportedFindingsWizardRow>
    );
}

export { RequestQuoteWizardRow, ToggleTooltipFallback };
